import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Camera, ClipboardList, LifeBuoy } from 'lucide-react';
import { Dialog } from '../ui/Dialog/Dialog';
import { Button } from '../atoms/Button/Button';
import { Text } from '../atoms/Typography/Text';
import { VoiceRecorderButton } from '../atoms/VoiceRecorderButton';
import { Checkbox } from '../atoms/Checkbox';
import { useTranslation } from '../../i18n/useTranslation';
import { useCapture } from './CaptureContext';
import { VoiceExamples } from './VoiceExamples';
import './CaptureSheet.css';

/**
 * Capture sheet (wireframe "Capture Sheet"): the three ways to capture, opened
 * by the Capture tab. Record note has the focus, so recording is two taps away
 * on a phone; Log tests stays two taps away too (usability audit 4.2).
 * Emergency help (Release 2, UC-18) opens the first-aid checklist.
 */
export function CaptureSheet() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const capture = useCapture();
  const recordRef = useRef<HTMLButtonElement>(null);
  const { sheetOpen, aquariumId, aquariumName } = capture;

  useEffect(() => {
    if (!sheetOpen) return undefined;
    // After the Dialog moved focus to itself.
    const id = setTimeout(() => recordRef.current?.focus(), 0);
    return () => clearTimeout(id);
  }, [sheetOpen]);

  const go = (path: string): void => {
    capture.closeCaptureSheet();
    navigate(path);
  };
  const aq = aquariumId === null ? null : `/aquariums/${encodeURIComponent(aquariumId)}`;

  return (
    <Dialog
      isOpen={sheetOpen}
      onClose={capture.closeCaptureSheet}
      title={String(t('capture.sheet.title'))}
      variant="sheet"
      size="sm"
      data-testid="capture-sheet"
    >
      {aq === null ? (
        <div className="capture-sheet">
          <Text>{t('capture.sheet.noAquarium')}</Text>
          <Button variant="primary" size="lg" fullWidth onClick={() => go('/aquariums/new')}>
            {t('capture.sheet.createAquarium')}
          </Button>
        </div>
      ) : (
        <div className="capture-sheet">
          {capture.available && (
            <div className="capture-sheet__record">
              <VoiceRecorderButton
                ref={recordRef}
                variant="primary"
                size="lg"
                fullWidth
                onStart={() => capture.startRecording({ aquariumId: aquariumId ?? undefined })}
              >
                {t('capture.sheet.recordNote')}
              </VoiceRecorderButton>
              <Checkbox
                label={t('capture.sheet.textOnly')}
                checked={capture.textOnly}
                onChange={(e) => capture.setTextOnly(e.target.checked)}
                data-testid="capture-text-only"
              />
              {capture.costHint !== null && (
                <Text as="span" variant="caption" color="muted">
                  {capture.costHint}
                </Text>
              )}
              <VoiceExamples />
            </div>
          )}
          <Button variant="secondary" size="lg" fullWidth onClick={() => go(`${aq}/measurements/session`)}>
            <span className="capture-sheet__option">
              <ClipboardList size={18} aria-hidden="true" focusable="false" />
              <span>{t('capture.sheet.logTests')}</span>
            </span>
          </Button>
          <Button variant="secondary" size="lg" fullWidth onClick={() => go(`${aq}/measurements/new`)}>
            <span className="capture-sheet__option">
              <Camera size={18} aria-hidden="true" focusable="false" />
              <span>{t('capture.sheet.photo')}</span>
            </span>
          </Button>
          {capture.voiceJournal === 'on' && (
            <Button variant="ghost" size="lg" fullWidth onClick={() => go(`${aq}/emergency`)}>
              <span className="capture-sheet__option">
                <LifeBuoy size={18} aria-hidden="true" focusable="false" />
                <span>{t('capture.sheet.emergency')}</span>
              </span>
            </Button>
          )}
          {aquariumName !== null && (
            <Text as="span" variant="caption" color="muted">
              {t('capture.sheet.tank', { tank: aquariumName })}
            </Text>
          )}
        </div>
      )}
    </Dialog>
  );
}
